import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { reportsAPI, usersAPI } from '@/lib/api';
import { formatDateTime, getActivityIcon, cn } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Download,
  Filter,
  Loader2,
  Phone,
  MessageSquare,
  Mail,
  StickyNote,
  Send,
  RefreshCw,
  XCircle,
  CheckCircle,
  ArrowUpDown,
  Clock,
  FileText,
  User,
  Users,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const ACTIVITY_TYPES = [
  { id: 'all', label: 'Tous les types' },
  { id: 'mail', label: 'Email' },
  { id: 'appel', label: 'Appel' },
  { id: 'sms', label: 'SMS' },
  { id: 'note', label: 'Note' },
  { id: 'devis_envoye', label: 'Devis envoyé' },
  { id: 'devis_relance', label: 'Relance' },
  { id: 'devis_cloture', label: 'Clôture' },
];

const ActivityReportPage = () => {
  const { isAdmin } = useAuth();
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    user_id: '',
    activity_type: '',
    date_from: '',
    date_to: '',
  });
  
  // Sorting
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [activitiesRes, usersRes] = await Promise.all([
        reportsAPI.getActivities(buildQueryParams()),
        isAdmin() ? usersAPI.getAll() : Promise.resolve({ data: [] }),
      ]);
      setActivities(activitiesRes.data);
      if (usersRes.data) {
        setUsers(usersRes.data);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des activités');
    } finally {
      setLoading(false);
    }
  };

  const buildQueryParams = () => {
    const params = {};
    if (filters.user_id) params.user_id = filters.user_id;
    if (filters.activity_type && filters.activity_type !== 'all') params.activity_type = filters.activity_type;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    return params;
  };

  const handleFilter = () => {
    fetchData();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await reportsAPI.exportActivities(buildQueryParams());
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `rapport_activites_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      toast.success('Export téléchargé');
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedActivities = [...activities].sort((a, b) => {
    let aVal = a[sortField] || '';
    let bVal = b[sortField] || '';
    
    if (sortField === 'created_at') {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    }
    return aVal < bVal ? 1 : -1;
  });

  const getActivityIconComponent = (type) => {
    const icons = {
      appel: Phone,
      sms: MessageSquare,
      mail: Mail,
      note: StickyNote,
      devis_envoye: Send,
      devis_relance: RefreshCw,
      devis_cloture: XCircle,
      devis_accepte: CheckCircle,
      devis_refuse: XCircle,
    };
    return icons[type] || Clock;
  };

  const getTypeLabel = (type) => {
    const labels = {
      appel: 'Appel',
      sms: 'SMS',
      mail: 'Email',
      note: 'Note',
      devis_envoye: 'Devis envoyé',
      devis_relance: 'Relance',
      devis_cloture: 'Clôture',
      devis_accepte: 'Accepté',
      devis_refuse: 'Refusé',
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="activity-report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Rapport d'activités
          </h1>
          <p className="text-slate-500 mt-1">
            {activities.length} activité(s) trouvée(s)
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting || activities.length === 0}
          className="bg-green-500 hover:bg-green-600 text-white"
          data-testid="export-activities-btn"
        >
          {exporting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Exporter CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3">
          <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
            <Filter className="w-4 h-4" />
            Filtres
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {/* User filter - only for admin */}
            {isAdmin() && (
              <div className="space-y-1">
                <Label className="text-xs">Utilisateur</Label>
                <Select
                  value={filters.user_id}
                  onValueChange={(value) => setFilters({ ...filters, user_id: value === 'all' ? '' : value })}
                >
                  <SelectTrigger data-testid="filter-user">
                    <SelectValue placeholder="Tous les utilisateurs" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les utilisateurs</SelectItem>
                    {users.map((user) => (
                      <SelectItem key={user.id} value={user.id}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Type filter */}
            <div className="space-y-1">
              <Label className="text-xs">Type d'activité</Label>
              <Select
                value={filters.activity_type || 'all'}
                onValueChange={(value) => setFilters({ ...filters, activity_type: value })}
              >
                <SelectTrigger data-testid="filter-type">
                  <SelectValue placeholder="Tous les types" />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_TYPES.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date from */}
            <div className="space-y-1">
              <Label className="text-xs">Du</Label>
              <Input
                type="date"
                value={filters.date_from}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                data-testid="filter-date-from"
              />
            </div>
            
            {/* Date to */}
            <div className="space-y-1">
              <Label className="text-xs">Au</Label>
              <Input
                type="date"
                value={filters.date_to}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                data-testid="filter-date-to"
              />
            </div>
            
            {/* Apply button */}
            <div className="flex items-end">
              <Button onClick={handleFilter} className="w-full" data-testid="apply-filters-btn">
                <Filter className="w-4 h-4 mr-2" />
                Appliquer
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Activities Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {activities.length === 0 ? (
            <div className="text-center py-12">
              <Clock className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune activité trouvée</p>
              <p className="text-sm text-slate-400 mt-1">
                Modifiez vos filtres pour voir plus de résultats
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Date
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('created_by_name')}
                    >
                      <div className="flex items-center gap-1">
                        Utilisateur
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => handleSort('client_name')}
                    >
                      <div className="flex items-center gap-1">
                        Client
                        <ArrowUpDown className="w-3 h-3" />
                      </div>
                    </TableHead>
                    <TableHead>N° Devis</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="max-w-md">Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedActivities.map((activity, idx) => {
                    const IconComponent = getActivityIconComponent(activity.type);
                    const iconConfig = getActivityIcon(activity.type);
                    
                    return (
                      <TableRow key={activity.id || idx}>
                        <TableCell className="whitespace-nowrap">
                          <span className="text-sm text-slate-600">
                            {formatDateTime(activity.created_at)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span className="font-medium text-slate-700">
                              {activity.created_by_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {activity.client_id ? (
                            <Link 
                              to={`/clients/${activity.client_id}`}
                              className="text-blue-600 hover:underline font-medium"
                            >
                              {activity.client_name}
                            </Link>
                          ) : (
                            <span className="text-slate-600">{activity.client_name}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {activity.devis_reference ? (
                            <Link 
                              to={`/admin/devis/${activity.devis_id}`}
                              className="font-mono text-sm text-blue-600 hover:underline"
                            >
                              {activity.devis_reference}
                            </Link>
                          ) : (
                            <span className="text-slate-400">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={cn("w-6 h-6 rounded-full flex items-center justify-center", iconConfig.color)}>
                              <IconComponent className="w-3 h-3" />
                            </div>
                            <Badge className={iconConfig.color}>
                              {getTypeLabel(activity.type)}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-md">
                          <p className="text-sm text-slate-600 truncate" title={activity.content}>
                            {activity.content}
                          </p>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary */}
      {activities.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Mail className="w-6 h-6 text-purple-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => a.type === 'mail' || a.type === 'devis_envoye').length}
              </p>
              <p className="text-xs text-slate-500">Emails</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <Phone className="w-6 h-6 text-blue-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => a.type === 'appel').length}
              </p>
              <p className="text-xs text-slate-500">Appels</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <RefreshCw className="w-6 h-6 text-orange-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => a.type === 'devis_relance').length}
              </p>
              <p className="text-xs text-slate-500">Relances</p>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4 text-center">
              <StickyNote className="w-6 h-6 text-amber-500 mx-auto mb-2" />
              <p className="text-2xl font-bold text-slate-800">
                {activities.filter(a => a.type === 'note').length}
              </p>
              <p className="text-xs text-slate-500">Notes</p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ActivityReportPage;
